/**
 * The OKR status vocabulary, once.
 *
 * S1-g (PRD § 14.2 decision 7) settled which statuses the API accepts. Before it, `closed`
 * — the most common status in the database, written whenever an OKR or its cycle is closed —
 * was rejected outright by the `?status=` filter, so users could not list closed OKRs at all.
 * Now they can, which means every component that renders a status badge will start seeing
 * `closed` where it never did before.
 *
 * Six separate status maps existed when that change landed and exactly one of them knew about
 * `closed`. The worst of the five fell back to `STATUS_BADGES.active`, so a closed OKR
 * rendered as a green **Active** badge — wrong, not merely unstyled.
 *
 * This module owns the two things that are genuinely ONE rule — the legal values and their
 * human labels. It deliberately does NOT own presentation: a Badge variant, a Tailwind class
 * pair and a chart hex are different media, and forcing them through one table made it worse
 * rather than better. Each consumer keeps its own colours and is checked against
 * `OKR_STATUSES` by `utils/__tests__/okrStatus.test.ts`, so a status with no rendering fails
 * by name instead of falling back to something misleading.
 *
 * Mirrors `backend/src/validators/okr.validator.ts`. The frontend may not import from the
 * backend, so this is a copy — the test above is what keeps the two honest. Change one and
 * you change both, in the same commit.
 */

/** Statuses a user may assign through the OKR create/edit form. */
export const OKR_STATUSES_WRITABLE = [
  'draft',
  'active',
  'on_track',
  'at_risk',
  'off_track',
  'completed',
] as const;

/**
 * Every status the API can return, and therefore every status a badge must be able to render.
 *
 * `closed` is written by closing an OKR or its cycle. `deleted` is written by the soft-delete
 * path, always alongside `deletedAt` — so no list endpoint ever returns it, but a status map
 * that omits it would fall back rather than fail if one ever leaked through.
 */
export const OKR_STATUSES = [...OKR_STATUSES_WRITABLE, 'closed', 'deleted'] as const;

export type OKRStatus = (typeof OKR_STATUSES)[number];

export const OKR_STATUS_LABELS: Record<OKRStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  on_track: 'On Track',
  at_risk: 'At Risk',
  off_track: 'Off Track',
  completed: 'Completed',
  closed: 'Closed',
  deleted: 'Deleted',
};

/**
 * Label for any status string, including one this build has never heard of.
 *
 * Production runs 14 real tenants whose rows this repo has not measured, so an unknown value
 * is title-cased rather than dropped. Showing `Some Status` is recoverable; showing nothing,
 * or showing another status's label, is not.
 */
export const okrStatusLabel = (status: string): string =>
  OKR_STATUS_LABELS[status as OKRStatus] ??
  status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
